/**
 * Audit events as sentences (C2-30): "Approved contract fact: Vendor",
 * "Created deliverable: D-04 Data conversion plan". One pure formatter for the
 * audit trail and the portfolio Activity feed, so neither shows an operation
 * string (`proposal_decide`), a table name (`Routing Proposal`) or an id. The
 * raw operation stays available for a tooltip through {@link auditOperationLabel}.
 */

export interface AuditTextEvent {
  /** The audit row's operation (`create`, `proposal_decide`, …). */
  event_type: string;
  resource_type: string;
  /** The record's name, when the API could find one. */
  resource_title?: string | null;
  /** The proposal type of a routing proposal, the register type of a register entry. */
  resource_subtype?: string | null;
  before?: unknown;
  after?: unknown;
}

/** What a resource type is called in a sentence. */
const RESOURCE_NOUNS: Record<string, string> = {
  ai_base_formation_run: 'AI document reading',
  ai_proposal: 'AI proposal',
  budget_entry: 'budget entry',
  change_order: 'change order',
  chat_message: 'chat message',
  checkpoint_assessment: 'checkpoint assessment',
  client: 'client',
  client_viewer_grant: 'viewer access',
  contract_fact: 'contract fact',
  deliverable: 'deliverable',
  deliverable_revision: 'deliverable revision',
  deviation: 'deviation',
  document: 'document',
  document_classification: 'document classification',
  document_source: 'document source',
  document_version: 'document version',
  domain_assessment: 'domain assessment',
  domain_rubric: 'domain rubric',
  engagement: 'engagement',
  engagement_archive: 'engagement archive',
  engagement_draft: 'engagement draft',
  engagement_methodology: 'methodology',
  engagement_requirement: 'requirement',
  engagement_requirement_set: 'requirement set',
  evidence_link: 'evidence link',
  finding: 'finding',
  health_domain: 'health domain',
  health_synthesis_report: 'synthesis report',
  interview_note: 'interview',
  issue_register: 'issue register',
  ivv_checkpoint: 'IV&V checkpoint',
  notification: 'notification',
  pdf_report: 'PDF report',
  process_map: 'process map',
  project: 'engagement',
  pulse: 'pulse report',
  readiness_criterion: 'readiness criterion',
  readiness_exec_summary: 'executive summary',
  readiness_rubric_config: 'readiness rubric',
  readiness_score: 'readiness score',
  readiness_scorecard: 'readiness scorecard',
  readiness_snapshot: 'readiness snapshot',
  record_lock: 'record lock',
  register_entry: 'register entry',
  rfi: 'RFI',
  risk_issue: 'RAID entry',
  risk_register: 'risk register',
  routing_proposal: 'AI proposal',
  routing_proposals_batch: 'AI proposals',
  schedule_milestone: 'milestone',
  search_index: 'search index',
  snapshot: 'snapshot',
  sow: 'contract baseline',
  template_version: 'template',
  user: 'user',
};

/** What each register type's entries are called. */
const REGISTER_NOUNS: Record<string, string> = {
  deliverable: 'deliverable',
  deviation: 'deviation',
  finding: 'finding',
  interview_note: 'interview',
  readiness_score: 'readiness score',
  rfi_register: 'RFI',
  risk_issue: 'RAID entry',
  budget_entry: 'budget entry',
};

/**
 * What each AI proposal type proposes: every value of the
 * ai_proposals.proposal_type CHECK (migration 0109), so no proposal reads
 * "AI proposed AI proposal" (C2-73).
 */
const PROPOSAL_NOUNS: Record<string, string> = {
  budget_entry: 'budget entry',
  change_order: 'change order',
  checkpoint: 'IV&V checkpoint',
  checkpoint_link: 'checkpoint link',
  checkpoint_score: 'checkpoint score',
  contract_fact: 'contract fact',
  deliverable_submission: 'deliverable submission',
  deviation: 'deviation',
  discovery_evidence: 'discovery evidence',
  discovery_pain_point: 'pain point',
  discovery_process_map: 'process map',
  discovery_question: 'discovery question',
  discovery_requirement: 'discovery requirement',
  domain_rating: 'domain rating',
  edl_item: 'deliverable',
  finding: 'finding',
  health_domain_set: 'health domain set',
  methodology: 'scoring model',
  milestone_update: 'milestone update',
  new_record: 'register entry',
  readiness_rubric: 'readiness rubric',
  readiness_score: 'readiness score',
  register_framework: 'register structure',
  register_update: 'register update',
  rubric: 'scoring rubric',
};

/**
 * How each operation reads: a past-tense verb before the record's noun, and
 * for a few an ending after it ("Held AI proposal for review").
 */
const OPERATION_VERBS: Record<string, Phrase> = {
  create: 'Created',
  update: 'Updated',
  upsert: 'Updated',
  delete: 'Deleted',
  override: 'Overrode',
  proposal: 'AI proposed',
  access_denied: 'Denied access to',
  dispatch: 'Sent',
  suppress: 'Suppressed',
  verification_transition: 'Changed the verification of',
  baseline_commit: 'Committed',
  checkpoint_archive: 'Archived',
  assessment_submit: 'Submitted',
  domain_config: 'Configured',
  domain_rating_commit: 'Rated',
  framework_bind: 'Applied a framework to',
  framework_promote_candidate: { verb: 'Proposed', after: 'for the firm library' },
  rubric_lock: 'Locked',
  finding_severity_change: 'Changed the severity of',
  xref_create: 'Linked',
  xref_detach: 'Unlinked',
  readiness_score_commit: 'Scored',
  scorecard_recompute: 'Recalculated',
  threshold_cross: 'Threshold crossed on',
  reeval_requested: 'Asked the AI to re-evaluate',
  reeval_run: 'AI re-evaluated',
  pdf_render: 'Rendered',
  exec_summary_draft: 'Drafted',
  exec_summary_publish: 'Published',
  pulse_draft: 'Drafted',
  pulse_publish: 'Published',
  synthesis_draft: 'Drafted',
  synthesis_section_edit: 'Edited a section of',
  synthesis_publish: 'Published',
  archive_build: 'Built',
  archive_handoff: 'Handed off',
  auto_accept_blocked: { verb: 'Held', after: 'for review' },
  schedule_milestone_create: 'Created',
  schedule_milestone_update: 'Updated',
  schedule_milestone_delete: 'Deleted',
  schedule_milestone_slipped: 'Recorded a slip on',
  budget_create: 'Created',
  budget_entry_create: 'Created',
  budget_entry_update: 'Updated',
  budget_entry_override: 'Overrode',
  budget_entry_delete: 'Deleted',
  'firm.notification_policy.updated': 'Updated',
  'brand.template.updated': 'Updated',
  'search_index.reindex_all': 'Rebuilt',
  pulse_posture_snapshotted: 'Recorded the posture of',
  curation_under_review: 'Started reviewing',
  curation_feedback_requested: 'Asked for feedback on',
  curation_returned: 'Returned',
  pdf_url_refresh: 'Refreshed the download link of',
  email_template_updated: 'Updated',
  citation_resolution_broken: 'Found a broken citation in',
  rubric_criterion_add: 'Added a criterion to',
  rubric_criterion_remove: 'Removed a criterion from',
  rubric_threshold_update: 'Changed the threshold of',
  engagement_draft_upsert: 'Saved',
  engagement_draft_delete: 'Discarded',
};

/** Operations whose subject is not the row's resource type. */
const OPERATION_NOUNS: Record<string, string> = {
  'firm.notification_policy.updated': 'notification policy',
  'brand.template.updated': 'brand template',
  email_template_updated: 'email template',
  'search_index.reindex_all': 'search index',
  budget_create: 'budget',
};

type Phrase = string | { verb: string; after: string };

/** How a routing proposal decision reads. */
const DECISION_PHRASES: Record<string, Phrase> = {
  approved: 'Approved',
  approved_as_noted: { verb: 'Approved', after: 'with notes' },
  rejected: 'Rejected',
  revise: { verb: 'Sent', after: 'back for revision' },
  revise_resubmit: { verb: 'Sent', after: 'back for revision' },
};

/** The judge's rationale when its new reading equals the saved value (packages/ai-workers judge.ts). */
const SUPERSEDED_MATCHES_SAVED = 'superseded:matches_saved';

/** What a superseded proposal matched: the saved rating, score or value. */
function savedWord(noun: string): string {
  if (noun.endsWith('rating')) return 'rating';
  if (noun.endsWith('score')) return 'score';
  return 'value';
}

/** "Verb noun ending: title". */
function sentence(phrase: Phrase, noun: string, title: string | null | undefined): string {
  return withTitle(
    typeof phrase === 'string' ? `${phrase} ${noun}` : `${phrase.verb} ${noun} ${phrase.after}`,
    title,
  );
}

/** Operations that move a record to a new status, written to `after.status`. */
const TRANSITION_OPERATIONS = new Set(['deliverable_transition', 'deviation_transition']);

function asObject(v: unknown): Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

function words(identifier: string): string {
  return identifier
    .replace(/[._-]+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The record's noun: its register or proposal type when known, else its resource type. */
export function auditResourceNoun(resourceType: string, subtype?: string | null): string {
  if (subtype) {
    if (resourceType === 'register_entry' && REGISTER_NOUNS[subtype]) {
      return REGISTER_NOUNS[subtype]!;
    }
    if (
      (resourceType === 'routing_proposal' || resourceType === 'ai_proposal') &&
      PROPOSAL_NOUNS[subtype]
    ) {
      return PROPOSAL_NOUNS[subtype]!;
    }
  }
  return RESOURCE_NOUNS[resourceType] ?? words(resourceType || 'record');
}

function withTitle(base: string, title: string | null | undefined): string {
  const t = text(title);
  return t ? `${base}: ${t}` : base;
}

/**
 * The event as one plain sentence. Never includes an id: an event whose
 * record has no name reads "Created deliverable", and the expanded audit row
 * still shows the record's id for whoever needs it.
 */
export function describeAuditEvent(event: AuditTextEvent): string {
  const op = event.event_type;
  const after = asObject(event.after);
  const title = event.resource_title ?? null;

  if (op === 'proposal_decide') {
    const decision = text(after.decision);
    const noun = auditResourceNoun('routing_proposal', event.resource_subtype);
    // C2-73: a judge re-reading an item retires its own earlier pending
    // proposal; the queue row is decided 'rejected' by the system with a
    // "superseded…" rationale. Nobody rejected anything.
    const rationale = text(after.decision_rationale);
    if (decision === 'rejected' && rationale?.toLowerCase().startsWith('superseded')) {
      return withTitle(
        rationale.toLowerCase() === SUPERSEDED_MATCHES_SAVED
          ? `AI retired its earlier ${noun} proposal (matches the saved ${savedWord(noun)})`
          : `AI replaced its earlier ${noun} proposal`,
        title,
      );
    }
    const phrase =
      after.auto_approved === true && (decision === 'approved' || decision === 'approved_as_noted')
        ? 'Auto-approved'
        : ((decision && DECISION_PHRASES[decision]) ?? 'Decided on');
    return sentence(phrase, noun, title);
  }

  if (op === 'access_denied') {
    return after.reason === 'cross_engagement_audit_view'
      ? 'Denied access to the audit trail'
      : withTitle(`Denied access to ${auditResourceNoun(event.resource_type)}`, title);
  }

  const noun =
    OPERATION_NOUNS[op] ?? auditResourceNoun(event.resource_type, event.resource_subtype);

  if (TRANSITION_OPERATIONS.has(op)) {
    const status = text(after.to_status) ?? text(after.status);
    return withTitle(
      status ? `Moved ${noun} to ${words(status)}` : `Changed the status of ${noun}`,
      title,
    );
  }

  // A few verbs read better for one resource: a snapshot is captured, a
  // document is uploaded.
  if (op === 'create' && event.resource_type === 'snapshot')
    return withTitle('Captured snapshot', title);
  if (op === 'create' && event.resource_type === 'document') {
    return withTitle('Uploaded document', title);
  }

  // An operation added after this list still reads as a sentence, never the raw string.
  return sentence(OPERATION_VERBS[op] ?? 'Changed', noun, title);
}

/**
 * The raw audit vocabulary, for a tooltip on the sentence: the operation and
 * the resource type as the audit log stores them ("proposal_decide ·
 * routing_proposal"). Auditors searching the export keep the exact terms.
 */
export function auditOperationLabel(
  event: Pick<AuditTextEvent, 'event_type' | 'resource_type'>,
): string {
  return `${event.event_type} · ${event.resource_type}`;
}
